/*
 * Copyright (c) 2020 wlocalhost.org.
 * All rights reserved.
 */

import { Inject, Injectable } from '@angular/core';

import { IP_TEMPLATES_TEMPORARY_STORAGE } from '../../tokens/tokens';
import { ETemplatesStorage, IUserTemplate, IUserTemplateCategory } from '../../interfaces/interfaces';

/**
 * This service allow you rewrite the default Builder Storage methods. It contains all methods builder needs to cache or store objects.
 * Alternatively you can rewrite the [IP_TEMPLATES_TEMPORARY_STORAGE]{@link IP_TEMPLATES_TEMPORARY_STORAGE}
 * or [IP_STORAGE_FACTORY]{@link IP_STORAGE_FACTORY} tokens.
 *
 * In next major updates, it can have some limitations for Regular License.
 *
 * @example
 * // Create a custom service
 * class YourOwnStorageService extends IpStorageService {}
 *
 * // Rewrite it into AppModule
 * { provide: IpStorageService, useClass: YourOwnStorageService }
 */
@Injectable()
export abstract class IpStorageService {
  constructor(
    /**
     * Storage for templates defined by [IP_TEMPLATES_TEMPORARY_STORAGE]{@link IP_TEMPLATES_TEMPORARY_STORAGE} token.
     */
    @Inject(IP_TEMPLATES_TEMPORARY_STORAGE) public templateStorage: Storage
  ) {}

  /**
   * Get the latest used templates end user has chosen from templates list.
   * @return Latest used templates by end user.
   */
  getLatestUsedTemplates(): IUserTemplate[] {
    return JSON.parse(this.templateStorage.getItem(ETemplatesStorage.LATEST_USED)) || [];
  }

  /**
   * Add a template to end user's chosen templates list.
   * @param template Template end user chosen.
   */
  addTemplateToLatestUsed(template: IUserTemplate): void {
    const latest = this.getLatestUsedTemplates().filter(({ title }) => title !== template.title);
    latest.unshift(template);
    this.templateStorage.setItem(ETemplatesStorage.LATEST_USED, JSON.stringify(latest));
  }

  /**
   * Clear all latest used templates cache.
   */
  clearLatestUsed(): void {
    this.templateStorage.removeItem(ETemplatesStorage.LATEST_USED);
  }

  /**
   * Cache a fully templates list.
   * @param list Template list to cache
   */
  cacheTemplateList(list: IUserTemplateCategory[]): void {
    this.templateStorage.setItem(ETemplatesStorage.STORAGE, JSON.stringify(list));
  }

  /**
   * Get a cached templates list if exists.
   * @return Either a cached templates list or an empty array.
   */
  getCachedTemplateList(): IUserTemplateCategory[] {
    return JSON.parse(this.templateStorage.getItem(ETemplatesStorage.STORAGE)) || [];
  }

  /**
   * Clear all templates list cache.
   */
  clearCachedTemplateList(): void {
    this.templateStorage.removeItem(ETemplatesStorage.STORAGE);
  }
}
